import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '../common/Input';
import { Textarea } from '../common/Textarea';
import { Button } from '../common/Button';
import { TagInput } from '../tag/TagInput';
import { LocationPicker } from '../tag/LocationPicker';
import { DateTimePicker } from '../tag/DateTimePicker';
import type { Post, CreatePostRequest, UpdatePostRequest, PostTagRequest, TagType, SightingType, PairingType } from '../../types/post';
import { getLocalizedSightingTypeLabel, SIGHTING_TYPE_OPTIONS, PAIRING_TYPE_OPTIONS, getLocalizedPairingTypeLabel } from '../../types/post';
import { searchTags } from '../../api/tags';
import { useLanguage } from '../../i18n/useLanguage';

const TITLE_MAX = 100;
const CONTENT_MAX = 1000;

const createPostSchema = (t: (key: any) => string) =>
  z.object({
    title: z.string().min(1, t('validation.titleRequired')).max(TITLE_MAX, t('validation.titleMax')),
    content: z.string().min(1, t('validation.contentRequired')).max(CONTENT_MAX, t('validation.contentMax')),
  });

type PostFormData = z.infer<ReturnType<typeof createPostSchema>>;

interface PostEditorProps {
  post?: Post;
  onSubmit: (data: CreatePostRequest | UpdatePostRequest) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  /**
   * Synchronous click hook on the submit button. Runs BEFORE react-hook-form's
   * async validation, which lets callers do things that require user-activation
   * (e.g. window.open for OIDC login in a new tab) without losing the gesture.
   */
  onSubmitClick?: () => void;
  /**
   * When true, location (lat/lon) and date/time are required to submit.
   * Used by the create flow; edit flow keeps them optional so legacy posts
   * without those fields stay editable.
   */
  requireEncounterDetails?: boolean;
}

export const PostEditor: React.FC<PostEditorProps> = ({
  post,
  onSubmit,
  onCancel,
  isLoading = false,
  onSubmitClick,
  requireEncounterDetails = false,
}) => {
  const { language: preferredLanguage, t } = useLanguage();
  const sightingLanguage = preferredLanguage === 'de' ? 'de' : 'en';
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<PostFormData>({
    resolver: zodResolver(createPostSchema(t)),
    defaultValues: {
      title: post?.title || '',
      content: post?.content || '',
    },
  });

  // Tag state
  const [generalTags, setGeneralTags] = useState<string[]>([]);
  const [locationLat, setLocationLat] = useState<number | undefined>();
  const [locationLon, setLocationLon] = useState<number | undefined>();
  const [locationName, setLocationName] = useState<string | undefined>();
  const [dateTime, setDateTime] = useState<string | undefined>();
  const [sightingType, setSightingType] = useState<SightingType | undefined>(post?.sightingType);
  const [sightingTypeError, setSightingTypeError] = useState<string | undefined>();
  const [pairingType, setPairingType] = useState<PairingType | undefined>(post?.pairingType);
  const [locationError, setLocationError] = useState<string | undefined>();
  const [dateTimeError, setDateTimeError] = useState<string | undefined>();

  useEffect(() => {
    if (post) {
      reset({
        title: post.title || '',
        content: post.content,
      });
      setSightingType(post.sightingType);
      setPairingType(post.pairingType);

      // Load existing tags
      if (post.tags) {
        const generalTagList = post.tags
          .filter(t => t.tagType === 'GENERAL')
          .map(t => t.tagValue)
          .filter((v): v is string => !!v);
        const locationTag = post.tags.find(t => t.tagType === 'LOCATION');
        const dateTimeTag = post.tags.find(t => t.tagType === 'DATE_TIME');
        
        setGeneralTags(generalTagList);
        if (locationTag) {
          setLocationLat(locationTag.locationLatitude);
          setLocationLon(locationTag.locationLongitude);
          setLocationName(locationTag.locationName);
        }
        if (dateTimeTag) setDateTime(dateTimeTag.dateTime);
      }
    } else {
      setSightingType(undefined);
      setPairingType(undefined);
    }
  }, [post, reset]);

  const handleFormSubmit = async (data: PostFormData) => {
    let hasError = false;
    if (!sightingType) {
      setSightingTypeError(t('post.sightingTypeRequired'));
      hasError = true;
    } else {
      setSightingTypeError(undefined);
    }

    if (requireEncounterDetails) {
      if (locationLat === undefined || locationLon === undefined) {
        setLocationError(t('validation.locationRequired'));
        hasError = true;
      } else {
        setLocationError(undefined);
      }
      if (!dateTime) {
        setDateTimeError(t('validation.dateTimeRequired'));
        hasError = true;
      } else {
        setDateTimeError(undefined);
      }
    } else {
      setLocationError(undefined);
      setDateTimeError(undefined);
    }

    if (hasError) return;

    const tags: PostTagRequest[] = [];
    
    generalTags.forEach(tagValue => {
      tags.push({
        tagType: 'GENERAL' as TagType,
        tagValue,
      });
    });
    
    if (locationLat !== undefined && locationLon !== undefined) {
      // Backend requires tagValue for location tags - use locationName or coordinates as fallback
      const tagValue = locationName || `${locationLat},${locationLon}`;
      tags.push({
        tagType: 'LOCATION' as TagType,
        tagValue: tagValue,
        latitude: locationLat,
        longitude: locationLon,
        locationName: locationName,
      });
    }
    
    if (dateTime) {
      tags.push({
        tagType: 'DATE_TIME' as TagType,
        tagValue: dateTime,
        dateTime: dateTime,
      });
    }
    
    const postLanguage = (post?.language || preferredLanguage)?.toLowerCase();
    const submitData: CreatePostRequest | UpdatePostRequest = {
      ...data,
      language: postLanguage && postLanguage !== 'en' ? postLanguage : undefined,
      sightingType: sightingType || undefined,
      pairingType: pairingType || undefined,
      tags: tags, // Always send the tags array, even if empty
    };
    
    await onSubmit(submitData);
  };

  const titleValue = watch('title') ?? '';
  const contentValue = watch('content') ?? '';

  const handleLocationChange = (lat?: number, lon?: number, name?: string) => {
    setLocationLat(lat);
    setLocationLon(lon);
    setLocationName(name);
    if (lat !== undefined && lon !== undefined) {
      setLocationError(undefined);
    }
  };

  const handleDateTimeChange = (value?: string) => {
    setDateTime(value);
    if (value) {
      setDateTimeError(undefined);
    } else {
      setDateTimeError(requireEncounterDetails ? t('validation.dateTimeRequired') : undefined);
    }
  };

  return (
    <form className="post-editor" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <div className="form-group-with-counter">
        <Input
          label={t('post.title')}
          {...register('title')}
          error={errors.title?.message}
          placeholder={t('post.titlePlaceholder')}
          maxLength={TITLE_MAX}
        />
        <span className={`char-counter ${titleValue.length > TITLE_MAX ? 'char-counter--over' : ''}`}>
          {titleValue.length}/{TITLE_MAX}
        </span>
      </div>

      <DateTimePicker
        value={dateTime}
        onChange={handleDateTimeChange}
        error={dateTimeError}
        clearButton="none"
      />

      <LocationPicker
        latitude={locationLat}
        longitude={locationLon}
        locationName={locationName}
        onLocationChange={handleLocationChange}
        error={locationError}
      />

      <div className="form-group-with-counter">
        <Textarea
          label={t('post.content')}
          {...register('content')}
          error={errors.content?.message}
          placeholder={t('post.contentPlaceholder')}
          rows={10}
          maxLength={CONTENT_MAX}
        />
        <span className={`char-counter ${contentValue.length > CONTENT_MAX ? 'char-counter--over' : ''}`}>
          {contentValue.length}/{CONTENT_MAX}
        </span>
      </div>

      <div className="post-editor-type-row">
        <div
          className={`post-editor-sighting-type form-group${sightingTypeError ? ' post-editor-sighting-type--required' : ''}`}
        >
          <label className="form-label">{t('post.sightingType')}</label>
          <div className="sighting-type-options" role="group" aria-label={t('post.sightingType')}>
            {SIGHTING_TYPE_OPTIONS.map((opt) => (
              <label key={opt.value} className="sighting-type-option">
                <input
                  type="radio"
                  name="sightingType"
                  value={opt.value}
                  checked={sightingType === opt.value}
                  onChange={() => { setSightingType(opt.value as SightingType); setSightingTypeError(undefined); }}
                />
                <span className="sighting-type-label">
                  {opt.emoji} {getLocalizedSightingTypeLabel(opt.value, sightingLanguage)}
                </span>
              </label>
            ))}
          </div>
          {sightingTypeError && (
            <p className="post-editor-sighting-type-hint">{sightingTypeError}</p>
          )}
        </div>

        <div className="post-editor-pairing-type form-group">
          <label className="form-label">{t('post.pairingTypeHeading')}</label>
          <div
            className="sighting-type-options"
            role="radiogroup"
            aria-label={t('post.pairingTypeHeading')}
          >
            {PAIRING_TYPE_OPTIONS.map((opt) => (
              <label key={opt.value} className="sighting-type-option">
                <input
                  type="radio"
                  name="pairingType"
                  value={opt.value}
                  checked={pairingType === opt.value}
                  onChange={() => setPairingType(opt.value as PairingType)}
                />
                <span className="sighting-type-label">
                  {getLocalizedPairingTypeLabel(opt.value, sightingLanguage) ?? opt.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="post-editor-tags">
        <div className="form-group">
          <label htmlFor="general-tags">{t('post.tags')}</label>
          <TagInput
            value={generalTags}
            onChange={setGeneralTags}
            onSearch={searchTags}
            placeholder={t('tag.inputPlaceholder')}
            maxTags={10}
          />
          <p className="form-helper">{t('post.tagsHelper')}</p>
        </div>
      </div>

      <div className="post-editor-actions">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          onClick={onSubmitClick}
        >
          {post ? t('post.updateButton') : t('post.createButton')}
        </Button>
      </div>
    </form>
  );
};

